import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, EmptyState } from "../../components/Common";
import { CommonDashboard } from "../../components/CommonDashboard";
import { SetupChecklist } from "../../components/SetupChecklist";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, BarChart3, Calendar, Stethoscope, Users, ClipboardList, Receipt, FileText, ExternalLink, Plus, Trash2, Briefcase, Download, Search, PhoneCall, Sparkles, Activity, Settings } from "lucide-react";
import { usePermissions } from "../../lib/permissions";
import { useAuth } from "../../lib/auth";
import StaffManagement from "./StaffManagement";
import { ClinicBillingWorkspace } from "../../components/ClinicBillingWorkspace";
import ClinicReportsPage from "./ClinicReportsPage";
import { VisitTodayWorkspace } from "../../components/VisitTodayWorkspace";

const ALL_LINKS = [
  { to: "/clinic-admin", label: "Overview", icon: <BarChart3 className="w-4 h-4" />, end: true, perm: "dashboard.view" },
  { to: "/clinic-admin/appointments", label: "Appointments", icon: <Calendar className="w-4 h-4" />, perm: "appointments.view" },
  { to: "/clinic-admin/queue", label: "Visits & Walk-ins", icon: <Activity className="w-4 h-4" />, perm: "appointments.view" },
  { to: "/clinic-admin/encounters", label: "Encounters", icon: <Activity className="w-4 h-4" />, perm: "encounters.view" },
  { to: "/clinic-admin/doctors", label: "Doctors", icon: <Stethoscope className="w-4 h-4" />, perm: "doctors.view" },
  { to: "/clinic-admin/services", label: "Services", icon: <Briefcase className="w-4 h-4" />, perm: "services.view" },
  { to: "/clinic-admin/followups", label: "Follow-ups", icon: <PhoneCall className="w-4 h-4" />, perm: "followups.view" },
  { to: "/clinic-admin/staff", label: "Staff", icon: <Users className="w-4 h-4" />, perm: "staff.view" },
  { to: "/clinic-admin/patients", label: "Patients", icon: <ClipboardList className="w-4 h-4" />, perm: "patients.view" },
  { to: "/clinic-admin/users", label: "Users", icon: <Users className="w-4 h-4" />, perm: "users.view" },
  { to: "/clinic-admin/billing", label: "Billing", icon: <Receipt className="w-4 h-4" />, perm: "billing.view" },
  { to: "/clinic-admin/reports", label: "Reports", icon: <FileText className="w-4 h-4" />, perm: "reports.revenue" },
  { to: "/clinic-admin/settings", label: "Settings", icon: <Settings className="w-4 h-4" />, perm: "settings.view" },
];

function useFilteredLinks(rawLinks) {
  const { perms } = usePermissions();
  const all = perms.has("*");
  return rawLinks.filter((l) => !l.perm || all || perms.has(l.perm));
}

// Static export retained for backward-compat; pages should use `useClinicLinks()` instead.
export const links = ALL_LINKS;

export function useClinicLinks() {
  return useFilteredLinks(ALL_LINKS);
}

export function ClinicOverview() {
  const { user } = useAuth();
  const filteredLinks = useClinicLinks();
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/clinic/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);
  return (
    <DashboardLayout links={filteredLinks} title="Clinic Operations">
      {["clinic_admin", "super_admin"].includes(user?.role) && <SetupChecklist />}
      {profile && (
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">Public booking page</div>
            <a href={`/clinic/${profile.slug}`} target="_blank" rel="noreferrer" className="text-sm font-bold text-mg-medical inline-flex items-center gap-1.5 truncate" data-testid="booking-link">
              /clinic/{profile.slug} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
          </div>
          <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/clinic/${profile.slug}`) && toast.success("Link copied")} className="mg-btn-secondary text-xs">
            Copy link
          </button>
        </div>
      )}
      <CommonDashboard basePath="/clinic-admin" />
    </DashboardLayout>
  );
}

export function ClinicVisitQueue() {
  const filteredLinks = useClinicLinks();
  return <DashboardLayout links={filteredLinks} title="Visits & Walk-ins"><VisitTodayWorkspace mode="admin" embedded /></DashboardLayout>;
}

export function ClinicProfilePage() {
  const filteredLinks = useClinicLinks();
  const [form, setForm] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/clinic/profile"), api.get("/clinic/timezones"), api.get("/public/directory/departments")]).then(([p, t, d]) => {
      setForm({ ...p.data, timezone: p.data.timezone || "Asia/Kolkata" });
      setTimezones(t.data.timezones || []);
      setDepartmentOptions(d.data.items || []);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Unable to load clinic profile"));
  }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/clinic/profile", form); toast.success("Clinic profile saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const useCurrentLocation = () => {
    if (!navigator.geolocation) return toast.error("Location is not supported by this browser");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => { setForm((current) => ({ ...current, latitude: Number(coords.latitude.toFixed(6)), longitude: Number(coords.longitude.toFixed(6)) })); setLocating(false); },
      () => { toast.error("Location permission was not granted. Enter coordinates manually."); setLocating(false); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };
  const uploadLogo = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size > 2 * 1024 * 1024) return toast.error("Choose a PNG, JPEG or WebP logo up to 2 MB");
    const reader = new FileReader(); setUploadingLogo(true);
    reader.onload = async () => { try { const response = await api.post("/clinic/profile/logo", { data_url: reader.result }); setForm((current) => ({ ...current, logo_url: response.data.logo_url })); toast.success("Clinic logo uploaded"); } catch (error) { toast.error(error?.response?.data?.detail || "Logo upload failed"); } finally { setUploadingLogo(false); } };
    reader.onerror = () => { setUploadingLogo(false); toast.error("Logo could not be read"); };
    reader.readAsDataURL(file);
  };
  const hasCoordinates = form?.latitude !== undefined && form?.latitude !== null && form?.longitude !== undefined && form?.longitude !== null;
  const mapUrl = hasCoordinates ? `https://www.openstreetmap.org/export/embed.html?bbox=${Number(form.longitude)-0.015}%2C${Number(form.latitude)-0.01}%2C${Number(form.longitude)+0.015}%2C${Number(form.latitude)+0.01}&layer=mapnik&marker=${form.latitude}%2C${form.longitude}` : "";
  const logoSrc = form?.logo_url?.startsWith("/") ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${form.logo_url}` : form?.logo_url;
  return <DashboardLayout links={filteredLinks} title="Clinic Profile">{!form ? <div className="mg-card text-slate-500">Loading profile…</div> : <form onSubmit={save} className="mg-card max-w-3xl space-y-4">
    <div className="flex items-center gap-4 border-b pb-4"><div className="w-20 h-20 rounded-xl border bg-slate-50 overflow-hidden flex items-center justify-center">{logoSrc ? <img src={logoSrc} alt="Clinic logo" className="w-full h-full object-contain" /> : <Briefcase className="text-slate-300" />}</div><label className="mg-btn-secondary text-sm cursor-pointer">{uploadingLogo ? "Uploading…" : "Browse logo"}<input type="file" className="hidden" accept="image/png,image/jpeg,image/webp" disabled={uploadingLogo} onChange={(e) => uploadLogo(e.target.files?.[0])} /></label><span className="text-xs text-slate-500">PNG, JPEG or WebP · maximum 2 MB</span></div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><label className="text-sm font-semibold">Clinic name<input className="mg-input block w-full mt-1" value={form.name || ""} onChange={(e) => setForm({...form,name:e.target.value})} /></label><label className="text-sm font-semibold">Phone<input className="mg-input block w-full mt-1" value={form.phone || ""} onChange={(e) => setForm({...form,phone:e.target.value})} /></label><label className="text-sm font-semibold">Email<input type="email" className="mg-input block w-full mt-1" value={form.email || ""} onChange={(e) => setForm({...form,email:e.target.value})} /></label><label className="text-sm font-semibold">Website<input className="mg-input block w-full mt-1" value={form.website || ""} onChange={(e) => setForm({...form,website:e.target.value})} /></label></div>
    <label className="text-sm font-semibold block">Address<textarea className="mg-input block w-full mt-1" rows={2} value={form.address || ""} onChange={(e) => setForm({...form,address:e.target.value})} /></label>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{[["state","State"],["district","District"],["city","City / town"],["locality","Locality"],["postal_code","Postal code"],["landmark","Landmark"]].map(([key,label]) => <label key={key} className="text-sm font-semibold">{label}<input className="mg-input block w-full mt-1" value={form[key] || ""} onChange={(e) => setForm({...form,[key]:e.target.value})} /></label>)}</div>
    <section className="border rounded-xl p-4 space-y-3"><div className="flex flex-wrap justify-between gap-2"><div><h3 className="font-bold text-mg-navy">Map location</h3><p className="text-xs text-slate-500">Confirm the exact entrance patients should navigate to.</p></div><button type="button" onClick={useCurrentLocation} disabled={locating} className="mg-btn-secondary text-sm">{locating ? "Locating…" : "Use current location"}</button></div><div className="grid grid-cols-2 gap-3"><label className="text-sm font-semibold">Latitude<input type="number" step="0.000001" min="-90" max="90" className="mg-input block w-full mt-1" value={form.latitude ?? ""} onChange={(e) => setForm({...form,latitude:e.target.value === "" ? null : Number(e.target.value)})} /></label><label className="text-sm font-semibold">Longitude<input type="number" step="0.000001" min="-180" max="180" className="mg-input block w-full mt-1" value={form.longitude ?? ""} onChange={(e) => setForm({...form,longitude:e.target.value === "" ? null : Number(e.target.value)})} /></label></div>{hasCoordinates ? <iframe title="Clinic map preview" src={mapUrl} className="w-full h-64 rounded-xl border" loading="lazy" /> : <div className="h-32 rounded-xl bg-slate-50 flex items-center justify-center text-sm text-slate-400">Add coordinates or use your current location to preview the clinic marker.</div>}<label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={!!form.public_directory_enabled} disabled={!hasCoordinates} onChange={(e) => setForm({...form,public_directory_enabled:e.target.checked})} /><span><strong>Show this clinic in Find Clinics and on the public map</strong><span className="block text-xs text-slate-500">Turning this off hides the clinic and its doctors from public discovery. Existing booking links continue to work.</span></span></label></section>
    <label className="text-sm font-semibold block">Description<textarea className="mg-input block w-full mt-1" rows={3} value={form.description || ""} onChange={(e) => setForm({...form,description:e.target.value})} /></label>
    <fieldset className="border rounded-xl p-4"><legend className="px-2 text-sm font-bold text-mg-navy">Clinic departments</legend><p className="text-xs text-slate-500 mb-3">Choose every type of care offered at this clinic. Patients can use these departments to find you.</p><div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">{departmentOptions.map((department) => <label key={department} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer ${(form.departments || []).includes(department) ? "border-blue-300 bg-blue-50 text-blue-800" : "border-slate-200"}`}><input type="checkbox" checked={(form.departments || []).includes(department)} onChange={(e) => setForm({...form,departments:e.target.checked ? [...(form.departments || []),department] : (form.departments || []).filter((value) => value !== department)})} />{department}</label>)}</div></fieldset>
    <label className="text-sm font-semibold block">Clinic timezone<select className="mg-input block w-full mt-1" value={form.timezone} onChange={(e) => setForm({...form,timezone:e.target.value})} data-testid="clinic-timezone">{timezones.map((z) => <option key={z}>{z}</option>)}</select><span className="text-xs text-slate-500 font-normal">Doctor working hours and booking dates are interpreted in this timezone.</span></label>
    <section className="rounded-xl border border-teal-100 bg-teal-50/40 p-4" aria-labelledby="clinical-setup-heading">
      <h2 id="clinical-setup-heading" className="font-bold text-mg-navy">Clinical setup</h2>
      <p className="mt-1 text-xs text-slate-500">Choose the clinical charts available to clinicians in this clinic.</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="text-sm font-semibold">Clinic workflow
          <select className="mg-input mt-1 block w-full" value={form.clinical_chart_mode || "general"} onChange={(e) => setForm({...form, clinical_chart_mode:e.target.value})} data-testid="clinical-chart-mode">
            <option value="general">General medical</option><option value="dental">Dental</option><option value="both">General medical and dental</option>
          </select>
        </label>
        <label className="text-sm font-semibold">Tooth numbering
          <select className="mg-input mt-1 block w-full" value={form.dental_numbering_system || "fdi"} onChange={(e) => setForm({...form, dental_numbering_system:e.target.value})} disabled={(form.clinical_chart_mode || "general") === "general"} data-testid="dental-numbering-system">
            <option value="fdi">FDI / ISO 3950</option><option value="universal">Universal</option><option value="palmer">Palmer</option>
          </select>
        </label>
        <label className="text-sm font-semibold">Default dentition
          <select className="mg-input mt-1 block w-full" value={form.default_dentition || "age_assisted"} onChange={(e) => setForm({...form, default_dentition:e.target.value})} disabled={(form.clinical_chart_mode || "general") === "general"} data-testid="default-dentition">
            <option value="age_assisted">Age assisted</option><option value="permanent">Permanent</option><option value="primary">Primary</option>
          </select>
        </label>
        <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500">
          <input type="checkbox" checked={Boolean(form.periodontal_chart_enabled)} disabled className="h-4 w-4 accent-teal-600" data-testid="periodontal-chart-enabled" />
          Periodontal charting <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] uppercase">Coming later</span>
        </label>
      </div>
    </section>
    <div className="flex justify-end"><button className="mg-btn-primary" disabled={busy}>{busy ? "Saving…" : "Save profile"}</button></div>
  </form>}</DashboardLayout>;
}

export function ClinicDoctors() {
  const [doctors, setDoctors] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", specialization: "", qualification: "", consultation_fee: 500 });
  const refresh = () => api.get("/clinic/doctors").then((r) => setDoctors(r.data));
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try { const r = await api.post("/clinic/doctors", form); toast.success(r.data?.temp_password ? `Doctor added. Temporary password: ${r.data.temp_password}` : "Doctor added", { duration: 12000 }); setModal(false); setForm({ name: "", email: "", specialization: "", qualification: "", consultation_fee: 500 }); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <DashboardLayout links={links} title="Doctors">
      <div className="flex justify-end mb-4 gap-2">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/doctors.csv`} target="_blank" rel="noreferrer" className="mg-btn-secondary" data-testid="export-doctors-csv">
          <Download className="w-4 h-4" /> Export CSV
        </a>
        <button onClick={() => setModal(true)} className="mg-btn-primary" data-testid="add-doctor-btn"><Plus className="w-4 h-4" /> Add Doctor</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {doctors.map((d) => (
          <div key={d.doctor_id} className="mg-card" data-testid={`doctor-card-${d.doctor_id}`}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 mg-gradient-primary rounded-full flex items-center justify-center text-white font-bold text-xl">{d.name.charAt(3) || d.name.charAt(0)}</div>
              <div>
                <div className="font-bold text-mg-navy">{d.name}</div>
                <div className="text-xs text-slate-500">{d.specialization}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-600">
              <div>Qualification: <span className="text-mg-navy font-semibold">{d.qualification || "—"}</span></div>
              <div>Fee: <span className="text-mg-medical font-bold">₹{d.consultation_fee}</span></div>
              <div>Email: {d.email}</div>
            </div>
          </div>
        ))}
        {doctors.length === 0 && <EmptyState title="No doctors yet" message="Add your first doctor to start accepting appointments." />}
      </div>
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-mg-navy text-lg mb-4">Add doctor</h3>
            <div className="space-y-3">
              <input className="mg-input" placeholder="Dr. Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="doc-name" />
              <input className="mg-input" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="doc-email" />
              <input className="mg-input" placeholder="Specialization" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} data-testid="doc-spec" />
              <input className="mg-input" placeholder="Qualification" value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} />
              <input className="mg-input" placeholder="Consultation fee" type="number" value={form.consultation_fee} onChange={(e) => setForm({ ...form, consultation_fee: Number(e.target.value) })} />
            </div>
            <div className="text-xs text-slate-500 mt-2">A secure temporary password will be generated when the doctor is added.</div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setModal(false)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={save} className="mg-btn-primary flex-1 justify-center" data-testid="save-doctor">Add</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export function ClinicServices() {
  const [services, setServices] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", duration_minutes: 30, price: 500, color: "#2090CF", online_booking_enabled: true });
  const refresh = () => api.get("/clinic/services").then((r) => setServices(r.data));
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try { await api.post("/clinic/services", form); toast.success("Service added"); setModal(false); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/clinic/services/${id}`); refresh(); };
  return (
    <DashboardLayout links={links} title="Services">
      <div className="flex justify-end mb-4">
        <button onClick={() => setModal(true)} className="mg-btn-primary" data-testid="add-service-btn"><Plus className="w-4 h-4" /> Add Service</button>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Name</th><th>Duration</th><th>Price</th><th>Online</th><th></th></tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.service_id} className="border-b border-slate-50" data-testid={`service-row-${s.service_id}`}>
                <td className="py-3"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ background: s.color }} /><span className="font-semibold text-mg-navy">{s.name}</span></div><div className="text-xs text-slate-500">{s.description}</div></td>
                <td>{s.duration_minutes} min</td>
                <td className="font-semibold">₹{s.price}</td>
                <td>{s.online_booking_enabled ? "✓" : "—"}</td>
                <td className="text-right"><button onClick={() => del(s.service_id)} className="text-red-500 hover:text-red-700" aria-label={`Delete ${s.name}`}><Trash2 className="w-4 h-4" aria-hidden="true" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-mg-navy text-lg mb-4">Add service</h3>
            <div className="space-y-3">
              <input className="mg-input" placeholder="Service name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-name" />
              <textarea className="mg-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" className="mg-input" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} />
                <input type="number" className="mg-input" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} />
              </div>
              <input type="color" className="mg-input h-12" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.online_booking_enabled} onChange={(e) => setForm({ ...form, online_booking_enabled: e.target.checked })} /> Available for online booking</label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setModal(false)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={save} className="mg-btn-primary flex-1 justify-center" data-testid="save-service">Add</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export function ClinicStaff({ createOnLoad = false }) { const staffLinks = useClinicLinks(); return <StaffManagement createOnLoad={createOnLoad} links={staffLinks} />; }

function LegacyClinicStaff({ createOnLoad = false }) {
  const [staff, setStaff] = useState([]);
  const [modal, setModal] = useState(createOnLoad);
  const [form, setForm] = useState({ name: "", email: "" });
  const refresh = () => api.get("/clinic/staff").then((r) => setStaff(r.data));
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try { const r = await api.post("/clinic/staff", form); toast.success(r.data?.temp_password ? `Receptionist added. Temporary password: ${r.data.temp_password}` : "Staff added", { duration: 12000 }); setModal(false); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <DashboardLayout links={links} title="Receptionists & Staff">
      <div className="flex justify-end mb-4">
        <button onClick={() => setModal(true)} className="mg-btn-primary" data-testid="add-staff-btn"><Plus className="w-4 h-4" /> Add Receptionist</button>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Name</th><th>Email</th><th>Phone</th><th>Status</th></tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.user_id} className="border-b border-slate-50">
                <td className="py-3 font-semibold text-mg-navy">{s.name}</td>
                <td>{s.email}</td><td>{s.phone || "—"}</td>
                <td><span className="mg-status mg-status-active">{s.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-mg-navy text-lg mb-4">Add receptionist</h3>
            <input className="mg-input mb-3" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="mg-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <div className="text-xs text-slate-500 mt-2">A secure temporary password will be generated when the receptionist is added.</div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setModal(false)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={save} className="mg-btn-primary flex-1 justify-center">Add</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

// Sortable header cell — extracted so React doesn't re-create the component on every render
function SortableTh({ k, sortKey, sortDir, onToggle, children }) {
  return (
    <th onClick={() => onToggle(k)} className="cursor-pointer hover:text-mg-medical select-none">
      {children} {sortKey === k && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );
}

export function ClinicPatients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  useEffect(() => { api.get("/clinic/patients", { params: { q } }).then((r) => setList(r.data)); }, [q]);
  const sorted = React.useMemo(() => {
    const arr = [...list];
    arr.sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [list, sortKey, sortDir]);
  const toggle = (k) => sortKey === k ? setSortDir(sortDir === "asc" ? "desc" : "asc") : (setSortKey(k), setSortDir("asc"));
  return (
    <DashboardLayout links={links} title="Patients">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
          <input className="mg-input pl-9 max-w-md" placeholder="Search patients…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="patient-search" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{sorted.length} patient(s)</span>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/patients.csv`} target="_blank" rel="noreferrer" className="mg-btn-secondary text-sm" data-testid="export-patients-csv">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              <SortableTh k="name" sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Name</SortableTh>
              <SortableTh k="phone" sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Phone</SortableTh>
              <SortableTh k="email" sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Email</SortableTh>
              <SortableTh k="gender" sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Gender</SortableTh>
              <SortableTh k="blood_group" sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Blood</SortableTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.patient_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`patient-${p.patient_id}`}>
                <td className="py-3 font-semibold text-mg-navy"><a href={`/clinic-admin/patients/${p.patient_id}`} className="hover:text-mg-medical hover:underline">{p.name}</a></td>
                <td>{p.phone}</td><td>{p.email || "—"}</td><td>{p.gender || "—"}</td><td>{p.blood_group || "—"}</td>
              </tr>
            ))}
            {sorted.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-slate-400 text-sm">No patients found.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function ClinicAppointments() {
  const [list, setList] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState("");
  useEffect(() => { api.get("/appointments", { params: { date } }).then((r) => setList(r.data)); }, [date]);
  const filtered = statusFilter ? list.filter((a) => a.status === statusFilter) : list;
  const statuses = Array.from(new Set(list.map((a) => a.status)));
  return (
    <DashboardLayout links={links} title="Appointments">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mg-input max-w-xs" data-testid="appt-date-filter" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mg-input max-w-xs" data-testid="appt-status-filter">
          <option value="">All statuses ({list.length})</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">{filtered.length} of {list.length}</span>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/appointments.csv`} target="_blank" rel="noreferrer" className="mg-btn-secondary text-sm" data-testid="export-appointments-csv">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Time</th><th>Patient</th><th>Doctor</th><th>Service</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.appointment_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`appt-${a.appointment_id}`}>
                <td className="py-3 font-semibold">{new Date(a.appointment_datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                <td>{a.patient?.name}<div className="text-xs text-slate-500">{a.patient?.phone}</div></td>
                <td>{a.doctor?.name}</td>
                <td><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: a.service?.color }} />{a.service?.name}</div></td>
                <td><span className={`mg-status mg-status-${a.status.toLowerCase().replace(/\s+/g, "-")}`}>{a.status}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-slate-400 text-sm">No appointments for this date.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function ClinicBilling() {
  return <DashboardLayout links={links} title="Billing & Invoices"><ClinicBillingWorkspace allowTemplate /></DashboardLayout>;
}

function LegacyClinicBilling() {
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  useEffect(() => { api.get("/billing/invoices").then((r) => setInvoices(r.data)); }, []);
  const filtered = invoices.filter((i) => {
    if (statusFilter && i.status !== statusFilter) return false;
    if (q && !`${i.invoice_number} ${i.patient?.name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const statuses = Array.from(new Set(invoices.map((i) => i.status)));
  return (
    <DashboardLayout links={links} title="Billing & Invoices">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
          <input className="mg-input pl-9 max-w-xs" placeholder="Search invoice or patient…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoice-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mg-input max-w-xs" data-testid="invoice-status-filter">
          <option value="">All statuses ({invoices.length})</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">{filtered.length} of {invoices.length}</span>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/invoices.csv`} target="_blank" rel="noreferrer" className="mg-btn-secondary text-sm" data-testid="export-invoices-csv">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>
      <div className="mg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-3">Invoice</th><th>Patient</th><th>Date</th><th className="text-right">Total</th><th className="text-right">Paid</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.invoice_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`invoice-${i.invoice_id}`}>
                <td className="py-3 font-semibold text-mg-navy">{i.invoice_number}</td>
                <td>{i.patient?.name}</td>
                <td className="text-xs text-slate-500">{new Date(i.invoice_date).toLocaleDateString()}</td>
                <td className="text-right font-semibold">₹{i.total}</td>
                <td className="text-right text-mg-health font-semibold">₹{i.paid_amount}</td>
                <td><span className={`mg-status mg-status-${i.status.toLowerCase()}`}>{i.status}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-400 text-sm">No invoices match.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function ClinicReports() {
  return <ClinicReportsPage links={links} />;
}
